// src/features/chat/panel/PanelMessageList.tsx
// The stream itself: day separators, message rows with linkified text,
// attachment thumbnails/files, reaction chips, and the hover action bar
// (quick reactions, reply, reply in thread, create task) whose trailing "more"
// (⋮) button opens the shared MessageActionsMenu.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { EntityAvatar } from '@/components/EntityAvatar';
import {
  formatFileSize,
  getFileExtension,
  isImageAttachment,
} from '@/features/chat/logic';
import type { AttachmentPreview } from '@core/types/chat';
import { MessageActionsMenu } from '@/features/chat/MessageActionsMenu';
import { messageId, messageTs, renderLinkedText } from './panelShared';
const QUICK_EMOJIS = ['👍', '❤️', '😂'];

function dayLabel(ts: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(ts));
}

function timeLabel(ts: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(ts));
}

/** One image thumb; resolves its URL lazily through the orchestrator cache. */
function ImageThumb({
  preview,
  resolveUrl,
  onOpen,
}: {
  preview: AttachmentPreview;
  resolveUrl: (attachmentId: string) => Promise<string>;
  onOpen: () => void;
}) {
  const [url, setUrl] = useState(preview.thumbnail_url ?? '');
  const [loading, setLoading] = useState(!preview.thumbnail_url);

  useEffect(() => {
    if (!preview.thumbnail_url && preview.attachment?.id) {
      let cancelled = false;
      resolveUrl(preview.attachment.id)
        .then((u) => { if (!cancelled) { setUrl(u); setLoading(false); } })
        .catch(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }
  }, [preview.thumbnail_url, preview.attachment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="h-24 animate-pulse rounded-md bg-muted" aria-hidden="true" />;
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="block overflow-hidden rounded-md border border-border transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
      title={preview.file_name}
    >
      <img src={url} alt={preview.file_name} className="h-24 w-full object-cover" loading="lazy" />
    </button>
  );
}

/** Attachments of one message: images as a 2-up grid, files as compact rows. */
export function MessageAttachments({
  previews,
  resolveUrl,
  onOpenImage,
  onDownload,
}: {
  previews: AttachmentPreview[];
  resolveUrl: (attachmentId: string) => Promise<string>;
  onOpenImage: (attachmentId: string) => void;
  onDownload: (attachmentId: string, fileName: string) => void;
}) {
  const images = previews.filter((p) => isImageAttachment(p) && p.attachment?.id);
  const files = previews.filter((p) => !isImageAttachment(p) && p.attachment?.id);
  return (
    <div className="mt-1.5 space-y-1.5">
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-1.5">
          {images.map((p) => (
            <ImageThumb
              key={p.attachment!.id}
              preview={p}
              resolveUrl={resolveUrl}
              onOpen={() => onOpenImage(p.attachment!.id)}
            />
          ))}
        </div>
      )}
      {files.map((p) => (
        <button
          key={p.attachment!.id}
          type="button"
          onClick={() => onDownload(p.attachment!.id, p.file_name)}
          className="flex w-full max-w-xs items-center gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-primary/10 text-[10px] font-bold text-primary">
            {getFileExtension(p.file_name) || '?'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-foreground">{p.file_name}</p>
            <p className="text-[10px] tabular-nums text-muted-foreground">{formatFileSize(p.size_bytes)}</p>
          </div>
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
        </button>
      ))}
    </div>
  );
}

export function PanelMessageAnchor({
  message,
  renderAnchor,
  onAnchor,
}: {
  message: any;
  renderAnchor?: (message: any) => ReactNode;
  onAnchor?: (message: any) => void;
}) {
  const content = renderAnchor?.(message);
  if (content == null) return null;
  const className = 'mt-1 block w-full min-w-0 rounded-md border border-border bg-muted/40 px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  if (!onAnchor) return <div className={className}>{content}</div>;
  return (
    <button type="button" className={className} onClick={() => onAnchor(message)}>
      {content}
    </button>
  );
}

export interface PanelMessageAction {
  label: string;
  request: {
    content: string;
    metadata: Record<string, string>;
  };
  beforeRequest?: () => Promise<void>;
}

export type PanelMessageActionFor = (
  message: any,
) => PanelMessageAction | PanelMessageAction[] | null;

export async function runPanelMessageAction(
  action: PanelMessageAction,
  editMessage: () => Promise<any>,
): Promise<any> {
  await action.beforeRequest?.();
  return editMessage();
}

export function mergePanelMessageAction(current: any, action: PanelMessageAction, updated?: any): any {
  return {
    ...current,
    ...updated,
    metadata: {
      ...current?.metadata,
      ...action.request.metadata,
      ...updated?.metadata,
    },
  };
}

export function PanelMessageActionButton({
  message,
  action,
  loadingId,
  onAction,
}: {
  message: any;
  action?: PanelMessageAction | PanelMessageAction[] | null;
  loadingId?: string | null;
  onAction?: (message: any, action: PanelMessageAction) => void;
}) {
  const actions = !action ? [] : Array.isArray(action) ? action : [action];
  if (actions.length === 0 || !onAction) return null;
  const isLoading = loadingId === messageId(message);
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {actions.map((item) => (
        <Button
          key={item.label}
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          isLoading={isLoading}
          disabled={loadingId != null}
          onClick={() => onAction(message, item)}
        >
          {item.label}
        </Button>
      ))}
    </div>
  );
}

interface PanelRowHandlers {
  onReply: (msg: any) => void;
  onOpenThread: (msg: any) => void;
  onCreateTask: (msg: any) => void;
  onReaction: (msg: any, emoji: string) => void;
  onCopyLink: (msg: any) => void;
  onPin: (msg: any) => void;
  onUnpin: (msg: any) => void;
  onEdit: (msg: any) => void;
  onDelete: (msg: any) => void;
}

/** One message row. A component of its own so each row carries the state and
 *  anchor ref its "more" menu needs. */
function PanelMessageRow({
  msg,
  senderName,
  isOwn,
  isPinned,
  locale,
  resolveAttachmentUrl,
  onOpenImage,
  onDownload,
  handlers,
  renderAnchor,
  onAnchor,
  messageActionFor,
  messageActionLoadingId,
  onMessageAction,
}: {
  msg: any;
  senderName: string;
  isOwn: boolean;
  isPinned: boolean;
  locale: string;
  resolveAttachmentUrl: (attachmentId: string) => Promise<string>;
  onOpenImage: (attachmentId: string) => void;
  onDownload: (attachmentId: string, fileName: string) => void;
  handlers: PanelRowHandlers;
  renderAnchor?: (message: any) => ReactNode;
  onAnchor?: (message: any) => void;
  messageActionFor?: PanelMessageActionFor;
  messageActionLoadingId?: string | null;
  onMessageAction?: (message: any, action: PanelMessageAction) => void;
}) {
  const { t } = useI18n();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const ts = messageTs(msg);
  const previews: AttachmentPreview[] = msg?.attachment_previews?.items ?? [];
  const reactions: any[] = msg?.reactions?.items ?? [];

  return (
    <div data-message-id={messageId(msg)} className="group relative flex gap-2 py-1.5">
      <EntityAvatar name={senderName} image={msg?.sender?.avatar} profileId={msg?.sender?.entity?.id} size="sm" />
      <div className={`min-w-0 flex-1 rounded-md px-2 py-1 ${isOwn ? 'bg-muted' : ''}`}>
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm font-medium text-foreground">{senderName}</span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {ts ? timeLabel(ts, locale) : ''}
          </span>
        </div>
        <PanelMessageAnchor message={msg} renderAnchor={renderAnchor} onAnchor={onAnchor} />
        {msg?.content && (
          <p className="whitespace-pre-wrap break-words text-sm text-foreground">
            {renderLinkedText(msg.content)}
          </p>
        )}
        <PanelMessageActionButton
          message={msg}
          action={messageActionFor?.(msg)}
          loadingId={messageActionLoadingId}
          onAction={onMessageAction}
        />
        {previews.length > 0 && (
          <MessageAttachments
            previews={previews}
            resolveUrl={resolveAttachmentUrl}
            onOpenImage={onOpenImage}
            onDownload={onDownload}
          />
        )}
        {reactions.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {reactions.map((r: any) => (
              <button
                key={r.emoji}
                type="button"
                onClick={() => handlers.onReaction(msg, r.emoji)}
                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  r.me
                    ? 'border-primary/40 bg-accent text-foreground'
                    : 'border-border bg-muted/50 text-muted-foreground hover:border-primary/30'
                }`}
              >
                <span>{r.emoji}</span>
                <span className="tabular-nums">{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className={`absolute -top-2.5 right-1 z-10 flex items-center gap-0.5 rounded-md border border-border bg-background px-0.5 py-0.5 shadow-sm transition-opacity duration-150 ease-out motion-reduce:transition-none ${moreOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'}`}>
        {QUICK_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => handlers.onReaction(msg, emoji)}
            className="rounded p-1 text-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('chat.entityPanel.addReaction', { emoji })}
            title={t('chat.entityPanel.addReaction', { emoji })}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          onClick={() => handlers.onReply(msg)}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('chat.entityPanel.reply')}
          title={t('chat.entityPanel.reply')}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 17l-5-5 5-5" />
            <path d="M4 12h9a7 7 0 017 7v1" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => handlers.onOpenThread(msg)}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('chat.entityPanel.replyInThread')}
          title={t('chat.entityPanel.replyInThread')}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            <path d="M8 9h8" />
            <path d="M8 12h5" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => handlers.onCreateTask(msg)}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('chat.entityPanel.createTask')}
          title={t('chat.entityPanel.createTask')}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 12H3" />
            <path d="M16 6H3" />
            <path d="M16 18H3" />
            <path d="M18 9v6" />
            <path d="M21 12h-6" />
          </svg>
        </button>
        {/* Everything else — the shared message actions menu */}
        <button
          ref={moreButtonRef}
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('common.more')}
          title={t('common.more')}
          aria-haspopup="menu"
          aria-expanded={moreOpen}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
          </svg>
        </button>
      </div>
      <MessageActionsMenu
        anchor={moreButtonRef}
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        align="end"
        message={msg}
        isOwn={isOwn}
        isPinned={isPinned}
        onCopyLink={handlers.onCopyLink}
        onPin={handlers.onPin}
        onUnpin={handlers.onUnpin}
        onCreateTask={handlers.onCreateTask}
        onEdit={handlers.onEdit}
        onDelete={handlers.onDelete}
      />
    </div>
  );
}

export function PanelMessageList({
  messages,
  memberNames,
  myProfileId,
  locale,
  pinnedIds,
  resolveAttachmentUrl,
  onReply,
  onOpenThread,
  onCreateTask,
  onReaction,
  onOpenImage,
  onDownload,
  onCopyLink,
  onPin,
  onUnpin,
  onEdit,
  onDelete,
  renderAnchor,
  onAnchor,
  messageActionFor,
  messageActionLoadingId,
  onMessageAction,
}: {
  messages: any[];
  memberNames: Map<string, string>;
  myProfileId: string | null;
  locale: string;
  /** Ids of pinned messages — drives the Pin/Unpin toggle in the more menu. */
  pinnedIds?: ReadonlySet<string>;
  resolveAttachmentUrl: (attachmentId: string) => Promise<string>;
  onReply: (msg: any) => void;
  onOpenThread: (msg: any) => void;
  onCreateTask: (msg: any) => void;
  onReaction: (msg: any, emoji: string) => void;
  onOpenImage: (attachmentId: string) => void;
  onDownload: (attachmentId: string, fileName: string) => void;
  onCopyLink: (msg: any) => void;
  onPin: (msg: any) => void;
  onUnpin: (msg: any) => void;
  onEdit: (msg: any) => void;
  onDelete: (msg: any) => void;
  renderAnchor?: (message: any) => ReactNode;
  onAnchor?: (message: any) => void;
  messageActionFor?: PanelMessageActionFor;
  messageActionLoadingId?: string | null;
  onMessageAction?: (message: any, action: PanelMessageAction) => void;
}) {
  const { t } = useI18n();

  const handlers: PanelRowHandlers = {
    onReply,
    onOpenThread,
    onCreateTask,
    onReaction,
    onCopyLink,
    onPin,
    onUnpin,
    onEdit,
    onDelete,
  };

  let lastDay = '';
  const rows: React.ReactNode[] = [];
  for (const msg of messages) {
    const id = messageId(msg);
    const ts = messageTs(msg);
    const day = ts ? dayLabel(ts, locale) : '';
    if (day && day !== lastDay) {
      lastDay = day;
      rows.push(
        <div key={`day-${day}`} className="flex items-center gap-3 py-2" aria-hidden="true">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs tabular-nums text-muted-foreground">{day}</span>
          <div className="h-px flex-1 bg-border" />
        </div>,
      );
    }
    const senderId: string = msg?.sender?.entity?.id ?? '';
    const senderName: string = memberNames.get(senderId) || msg?.sender?.display_name?.trim() || t('chat.entityPanel.unknownSender');
    const isOwn = Boolean(myProfileId) && senderId === myProfileId;

    rows.push(
      <PanelMessageRow
        key={id || `${ts}-${rows.length}`}
        msg={msg}
        senderName={senderName}
        isOwn={isOwn}
        isPinned={id ? (pinnedIds?.has(id) ?? false) : false}
        locale={locale}
        resolveAttachmentUrl={resolveAttachmentUrl}
        onOpenImage={onOpenImage}
        onDownload={onDownload}
        handlers={handlers}
        renderAnchor={renderAnchor}
        onAnchor={onAnchor}
        messageActionFor={messageActionFor}
        messageActionLoadingId={messageActionLoadingId}
        onMessageAction={onMessageAction}
      />,
    );
  }
  return <>{rows}</>;
}
